import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  Output,
  ViewChild,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  forwardRef,
  ChangeDetectionStrategy,
  OnInit
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { ControlValueAccessorDirective } from '@shared/directives/control-value-accessor.directive';
import { Observable, debounceTime, distinctUntilChanged, map, tap } from 'rxjs';

export class DropdownItem {
  key: string | number | null;
  value: string | null;
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslateModule, ReactiveFormsModule, MatCardModule, FlexLayoutModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DropdownComponent)
    }
  ]
})
export class DropdownComponent<T> extends ControlValueAccessorDirective<T> implements OnChanges, OnInit {
  @Input() enableSearch = false;
  @Input() showLabel = true;
  @Input() isMultiselect = false;
  @Input() label: string;
  @Input() placeholder = 'Select any';
  @Input() clearText = 'Clear Selection';
  @Input() itemList: DropdownItem[] = [];
  @Input() customValidators: ValidatorFn[] = [];
  @Input() customSelectBehavior: boolean = false;

  @Input() defaultValue = { key: null, value: this.placeholder } as DropdownItem;
  @Output() selectEvent: EventEmitter<DropdownItem | null> = new EventEmitter();
  @Output() multiSelectEvent: EventEmitter<DropdownItem[] | null> = new EventEmitter();
  @ViewChild('controlInput') controlInput: ElementRef;
  @ViewChild('multiselectValueDisplay') multiselectValueDisplay: ElementRef;
  @ViewChild('itemsContainer') itemsContainer: ElementRef;

  controlObs: Observable<any> = new Observable();
  isExpanded = false;
  isSelected = false;
  itemListBackup: DropdownItem[] = [];

  selected?: boolean;
  selectedItems: DropdownItem[] = [];
  preventDropdownClosing = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemList.currentValue !== changes.itemList.previousValue) {
      const items: DropdownItem[] = changes.itemList.currentValue?.length > 0 ? changes.itemList.currentValue : [];
      if (!this.isMultiselect) {
        this.itemList = [this.defaultValue, ...items];
      }
      if (this.enableSearch) {
        this.itemListBackup = [...this.itemList];
      }
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.enableSearch) {
      this.controlObs = this.control.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        map(val =>
          val
            ? this.itemListBackup.filter(x => x.value?.toLowerCase()?.includes(val?.toLowerCase()))
            : this.itemListBackup
        ),
        tap(data => {
          this.isExpanded = true;
          this.itemList = data;

          if (this.isSelected) {
            this.isExpanded = false;
            this.isSelected = false;
          }
        })
      );
    }
  }

  override writeValue(obj: T): void {
    super.writeValue(obj);

    if (this.isMultiselect && Array.isArray(obj)) {
      this.selectedItems = obj
        .map(val => this.itemList.find(item => item.value === val))
        .filter((item): item is DropdownItem => !!item);
    }
  }

  valueChange(val: DropdownItem) {
    if (this.isMultiselect) {
      const index = this.selectedItems.findIndex(item => item.key === val.key);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(val);
      }
      this.multiSelectEvent.emit(this.selectedItems);
      if (!this.customSelectBehavior) {
        const valueArray = this.selectedItems.map(item => item.value);
        this.setNewValue(valueArray as T);
      }
    } else {
      if (this.control.value !== val.value) {
        this.selectEvent.emit(val);
        this.setNewValue((val.key !== null ? val.value : null) as T);
        this.isSelected = true;
      }
      this.isExpanded = false;
    }
  }

  displayValue() {
    if (!this.isMultiselect) {
      return (
        this.itemList.find(item => item.key === this.control.value || item.value === this.control.value)?.value ||
        this.placeholder);

    }
    return this.selectedItems.map(item => item.value).toString() || this.placeholder;
  }

  expandOrCollapse() {
    if (!this.isDisabled) this.isExpanded = !this.isExpanded;
  }

  clearSearch() {
    this.itemList = this.itemListBackup;
    this.valueChange(this.defaultValue);
    this.isSelected = false;
  }

  clearSelection() {
    this.selectedItems = [];
    this.setNewValue(null);
    this.multiSelectEvent.emit(this.selectedItems);
  }

  isItemChecked(item: DropdownItem): boolean {
    return this.selectedItems.some(selected => selected.key === item.key);
  }

  preventClosing() {
    this.preventDropdownClosing = true;
  }

  clickedOutside(event: Event) {
    if (!this.controlInput?.nativeElement.contains(event.target)) {
      const exists = Array.from((this.controlInput?.nativeElement as HTMLElement)?.children).find(
        item => item === event.target
      );
      if (!exists && !this.isMultiselect) {
        this.isExpanded = false;
      } else if (!exists && this.isMultiselect && !this.preventDropdownClosing) {
        this.isExpanded = false;
      } else {
        this.isTouched = true;
      }
      this.preventDropdownClosing = false;
    }
  }
}
